import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { getDestaques } from '../../services/destaqueService';
import { getImageUrl } from '../../services/sliderService';
import { Box, Typography, Card, CardMedia, Button, Skeleton } from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

const DestaquesInstituicao = ({ instituicaoId, modo = 'privado' }) => {
  const [destaques, setDestaques] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoadingStatus, setImageLoadingStatus] = useState({});

  useEffect(() => {
    const fetchDestaques = async () => {
      try {
        setLoading(true);
        const data = await getDestaques(instituicaoId, modo);
        console.log('Destaques recebidos:', data);
        setDestaques(data);
      } catch (error) {
        console.error('Erro ao buscar destaques:', error);
        setError('Falha ao carregar os destaques.');
      } finally {
        setLoading(false);
      }
    };

    if (instituicaoId) {
      fetchDestaques();
    }
  }, [instituicaoId, modo]);

  const handleImageLoad = (destaqueId) => {
    setImageLoadingStatus(prev => ({
      ...prev,
      [destaqueId]: 'success'
    }));
  };

  const handleImageError = (destaqueId, imagemUrl) => {
    console.log('Erro ao carregar imagem:', imagemUrl);
    setImageLoadingStatus(prev => ({
      ...prev,
      [destaqueId]: 'error'
    }));
  };

  const getFullImageUrl = (imagemUrl) => {
    try {
      if (!imagemUrl) return '';
      const url = getImageUrl(imagemUrl);
      console.log('URL processada:', url);
      return url;
    } catch (error) {
      console.error('Erro ao processar URL da imagem:', error);
      return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', py: 4 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!destaques || destaques.length === 0) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        backgroundColor: 'grey.50',
        borderRadius: 2
      }}>
        <Typography color="text.secondary">
          Nenhum destaque disponível.
        </Typography>
      </Box>
    );
  }

  const settings = {
    dots: true,
    infinite: destaques.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          infinite: destaques.length > 2
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          infinite: destaques.length > 1
        }
      }
    ]
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        pb: 5,
        '.slick-list': { 
          margin: '0 -8px' 
        },
        '.slick-slide > div': { 
          padding: '0 8px' 
        },
        '.slick-dots': {
          bottom: -35,
          li: {
            button: {
              '&:before': {
                fontSize: 8,
                color: 'primary.main',
                opacity: 0.4,
              }
            },
            '&.slick-active button:before': {
              opacity: 1,
            }
          }
        },
        '.slick-prev, .slick-next': {
          '&:before': {
            color: 'primary.main',
          }
        }
      }}
    >
      <Slider {...settings}>
        {destaques.map((destaque) => (
          <Box 
            key={destaque.id}
            sx={{
              height: '100%',
            }}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 1,
                transition: 'box-shadow 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  pt: '56.25%',
                  backgroundColor: 'grey.100',
                }}
              >
                {imageLoadingStatus[destaque.id] !== 'error' && (
                  <>
                    {imageLoadingStatus[destaque.id] !== 'success' && (
                      <Skeleton 
                        variant="rectangular" 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          transform: 'none'
                        }} 
                      />
                    )}
                    <CardMedia
                      component="img"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderBottom: '4px solid',
                        borderColor: 'primary.main',
                      }}
                      image={getFullImageUrl(destaque.imagemUrl)}
                      alt={destaque.titulo}
                      onLoad={() => handleImageLoad(destaque.id)}
                      onError={() => handleImageError(destaque.id, destaque.imagemUrl)}
                    />
                  </>
                )}
                {imageLoadingStatus[destaque.id] === 'error' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      borderBottom: '4px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Imagem não disponível
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box 
                sx={{ 
                  p: 2,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography 
                  variant="h6" 
                  component="h3"
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: '1.1rem',
                    mb: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {destaque.titulo}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    flexGrow: 1,
                    mb: 2,
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {destaque.descricao}
                </Typography>

                {destaque.link && (
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    href={destaque.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mt: 'auto',
                      textTransform: 'none',
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Saiba Mais
                  </Button>
                )}
              </Box>
            </Card>
          </Box>
        ))}
      </Slider>
    </Box>
  );
};

DestaquesInstituicao.propTypes = {
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  modo: PropTypes.oneOf(['publico', 'privado'])
};

DestaquesInstituicao.defaultProps = {
  modo: 'privado'
};

export default DestaquesInstituicao;